import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Paper,
  IconButton,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Autocomplete,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  Visibility,
  Favorite,
  Movie,
  AttachMoney,
  Refresh,
  Info,
  FilterList,
  Whatshot
} from '@mui/icons-material';
import { brandColors } from '../../../theme/theme';
import { 
  getFilmTrackingOverview, 
  getFilmTrackingStats,
  getDistributors,
  getProductionCompanies
} from '../services/filmTrackingService';
import { getDefaultStartDate, getDefaultEndDate, getQuickFilterRanges, formatDateForDisplay } from '../../../lib/utils/dateUtils';
import { useFilmSubscription } from '../../../hooks/useFilmSubscription';
import BlurredContent from '../../../components/Subscription/BlurredContent';
import { FILM_TIERS } from '../../../lib/filmSubscriptionTiers';
import FilmTile from '../../../components/FilmTracking/FilmTile';
import FilmDashboardBanner from '../../../components/FilmTracking/FilmDashboardBanner';
import boxOfficeProjectionSummaryService from '../../../lib/services/boxOfficeProjectionSummaryService';

const FilmTracking = () => {
  const navigate = useNavigate();
  const { canAccessDashboard } = useFilmSubscription();
  const [films, setFilms] = useState([]);
  const [stats, setStats] = useState({});
  const [metadata, setMetadata] = useState({});
  const [projectionSummaries, setProjectionSummaries] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const latestProjectionRequestRef = useRef(0);
  
  // Enhanced filter states
  const [filters, setFilters] = useState({
    startDate: getDefaultStartDate(),
    endDate: getDefaultEndDate(),
    sortBy: 'release_date',
    sortOrder: 'asc',
    searchTerm: '',
    distributors: [],
    productionCompanies: []
  });
  
  // Pending filters (not yet applied)
  const [pendingFilters, setPendingFilters] = useState({
    startDate: getDefaultStartDate(),
    endDate: getDefaultEndDate(),
    sortBy: 'release_date',
    sortOrder: 'asc',
    searchTerm: '',
    distributors: [],
    productionCompanies: []
  });
  
  // Company options for filters
  const [distributorOptions, setDistributorOptions] = useState([]);
  const [productionCompanyOptions, setProductionCompanyOptions] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  
  // Quick filter ranges
  const quickRanges = getQuickFilterRanges();

  const fetchData = async (customFilters = null) => {
    try {
      setLoading(true);
      setError(null);
      
      const currentFilters = customFilters || filters;
      
      // Use Promise.allSettled to handle errors gracefully - don't fail completely if one request fails
      const [overviewResult, statsResult] = await Promise.allSettled([
        getFilmTrackingOverview({
          startDate: currentFilters.startDate,
          endDate: currentFilters.endDate,
          sortBy: currentFilters.sortBy,
          sortOrder: currentFilters.sortOrder
        }),
        getFilmTrackingStats()
      ]);
      
      // Handle overview response
      let overviewResponse = null;
      if (overviewResult.status === 'fulfilled') {
        overviewResponse = overviewResult.value;
      } else {
        console.error('Failed to fetch overview:', overviewResult.reason);
        throw overviewResult.reason; // Throw the first error
      }
      
      // Handle stats response - make it optional so overview can still load
      let statsData = {};
      if (statsResult.status === 'fulfilled') {
        statsData = statsResult.value;
      } else {
        console.warn('Failed to fetch stats, continuing without them:', statsResult.reason);
        // Don't throw - allow page to load without stats
      }
      
      const filmsData = overviewResponse.films || [];
      setFilms(filmsData);
      setStats(statsData);
      setMetadata(overviewResponse.metadata || {});
      loadProjectionSummaries(filmsData);
    } catch (err) {
      console.error('Error fetching film tracking data:', err);
      
      // Provide specific error messages based on error type
      let errorMessage = 'Failed to load film tracking data. Please try again.';
      
      if (err.type === 'rateLimit' || err.status === 429) {
        errorMessage = 'Too many requests. The API is rate limiting. Please wait a moment and refresh the page.';
      } else if (err.type === 'server' || (err.status && err.status >= 500)) {
        errorMessage = 'Server error. Please try again in a moment.';
      } else if (err.type === 'network' || err.message?.includes('network')) {
        errorMessage = 'Network error. Please check your internet connection.';
      } else if (err.type === 'timeout' || err.message?.includes('timeout') || err.message?.includes('too long')) {
        errorMessage = 'Request timed out. The server is taking too long to respond. Please try again.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadProjectionSummaries = async (filmsList) => {
    const filmIds = Array.from(
      new Set(
        (filmsList || [])
          .map((film) => film.imdb_id || film.tt_id)
          .filter((id) => typeof id === 'string' && id.startsWith('tt'))
      )
    );

    const requestId = Date.now();
    latestProjectionRequestRef.current = requestId;

    if (filmIds.length === 0) {
      setProjectionSummaries({});
      return;
    }

    try {
      const summaries = await boxOfficeProjectionSummaryService.getProjectionSummaries(filmIds);
      if (latestProjectionRequestRef.current !== requestId) {
        return;
      }

      const projectionMap = {};
      filmIds.forEach((id) => {
        projectionMap[id] = summaries[id] || null;
      });
      setProjectionSummaries(projectionMap);
    } catch (projectionError) {
      console.error('Failed to load projection summaries:', projectionError);
      if (latestProjectionRequestRef.current === requestId) {
        setProjectionSummaries({});
      }
    }
  };

  // Apply client-side filters (search only)
  const getFilteredFilms = () => {
    let filtered = films;
    
    // Apply search filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(film => 
        (film.film_title || '').toLowerCase().includes(searchLower) ||
        (film.imdb_id || '').toLowerCase().includes(searchLower)
      );
    }
    
    return filtered;
  };
  
  const filteredFilms = getFilteredFilms();
  
  // Filter handlers
  const handleFilterChange = (key, value) => {
    // Update pending filters only (don't fetch yet)
    setPendingFilters({ ...pendingFilters, [key]: value });
  };
  
  const handleQuickFilter = (rangeKey) => {
    const range = quickRanges[rangeKey];
    if (range) {
      // Update pending filters with quick range
      setPendingFilters({
        ...pendingFilters,
        startDate: range.startDate,
        endDate: range.endDate
      });
    }
  };
  
  const handleApplyFilters = () => {
    // Apply pending filters and fetch data
    setFilters(pendingFilters);
    fetchData(pendingFilters);
  };
  
  const handleResetFilters = () => {
    const defaultFilters = {
      startDate: getDefaultStartDate(),
      endDate: getDefaultEndDate(),
      sortBy: 'release_date',
      sortOrder: 'asc',
      searchTerm: '',
      distributors: [],
      productionCompanies: []
    };
    setPendingFilters(defaultFilters);
    setFilters(defaultFilters);
    fetchData(defaultFilters);
  };

  // Clean film title by removing descriptions
  const cleanFilmTitle = (title) => {
    if (!title) return 'Unknown Film';
    
    // Remove common description patterns
    const patterns = [
      / – .*$/,  // Remove everything after " – "
      / - .*$/,  // Remove everything after " - "
      /: .*$/,   // Remove everything after ": " (but keep colons in titles)
      /\(.*\)$/, // Remove parenthetical descriptions
      /\s+$/,    // Remove trailing whitespace
    ];
    
    let cleanTitle = title;
    patterns.forEach(pattern => {
      cleanTitle = cleanTitle.replace(pattern, '');
    });
    
    return cleanTitle.trim();
  };

  useEffect(() => {
    fetchData();
    fetchCompanyOptions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch company options for filters
  const fetchCompanyOptions = async () => {
    try {
      setLoadingCompanies(true);
      const [distributors, productionCos] = await Promise.all([
        getDistributors(),
        getProductionCompanies()
      ]);
      setDistributorOptions(distributors);
      setProductionCompanyOptions(productionCos);
    } catch (error) {
      console.error('Failed to load company options:', error);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleFilmClick = (ttId) => {
    navigate(`/film-tracking/${ttId}`);
  };

  const getMetricColor = (value, type) => {
    // Use consistent metric-specific colors
    if (type === 'awareness') {
      return brandColors.awareness || '#1976d2';
    }
    if (type === 'interest') {
      return brandColors.highInterest || '#d32f2f';
    }
    if (type === 'theater') {
      return brandColors.theaterIntent || '#388e3c';
    }
    if (type === 'pay') {
      return brandColors.willingToPay || '#f57c00';
    }
    if (type === 'heat') {
      return '#ff5722'; // Orange-red for heat score
    }
    return brandColors.primary;
  };

  const FilmCard = ({ film }) => {
    const metrics = [
      {
        key: 'awareness',
        title: 'Awareness',
        value: film.awareness_pct || 0,
        subtitle: 'Know about film',
        icon: <Visibility fontSize="small" />,
        color: getMetricColor(film.awareness_pct || 0, 'awareness'),
        change: film.awareness_change
      },
      {
        key: 'interest',
        title: 'Interest',
        value: film.high_interest_pct || 0,
        subtitle: 'Want to see',
        icon: <Favorite fontSize="small" />,
        color: getMetricColor(film.high_interest_pct || 0, 'interest'),
        change: film.high_interest_change
      },
      {
        key: 'theater',
        title: 'Theater Intent',
        value: film.theater_intent_pct || 0,
        subtitle: 'Plan to see in theaters',
        icon: <Movie fontSize="small" />,
        color: getMetricColor(film.theater_intent_pct || 0, 'theater'),
        change: film.theater_intent_change
      },
      {
        key: 'heat',
        title: 'Heat Score',
        value: film.heat_pct || 0,
        subtitle: 'Overall buzz',
        icon: <Whatshot fontSize="small" />,
        color: getMetricColor(film.heat_pct || 0, 'heat'),
        change: null
      },
      {
        key: 'willingness_to_pay',
        title: 'Willing to Pay',
        value: film.willingness_to_pay_pct || 0,
        subtitle: 'Ready to spend',
        icon: <AttachMoney fontSize="small" />,
        color: getMetricColor(film.willingness_to_pay_pct || 0, 'pay'),
        change: film.willingness_to_pay_change
      },
      {
        key: 'plf',
        title: 'Premium Format',
        value: film.plf_pct || 0,
        subtitle: 'PLF interest',
        icon: <Movie fontSize="small" />,
        color: '#9c27b0',
        change: null
      }
    ];

    const footerLines = [
      film.total_responses ? `${film.total_responses.toLocaleString()} survey responses` : 'Survey responses unavailable',
      film.survey_period
        ? `Survey completed ${film.survey_period}`
        : film.survey_date
        ? `Survey completed ${formatDateForDisplay(film.survey_date)}`
        : null
    ].filter(Boolean);

    const projectionSummary = projectionSummaries[film.imdb_id || film.tt_id];

    const renderMetricsSection = ({ metrics: tileMetrics, clampPercentage }) => {
      const grid = (
        <Grid container spacing={1.5}>
          {tileMetrics.map((metric) => {
            const value = clampPercentage(metric.value);
            const color = metric.color || brandColors.primary;
            const displayValue = `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;

            return (
              <Grid item xs={12} sm={6} key={metric.key || metric.title}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: `${color}30`,
                    backgroundColor: `${color}08`,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.75
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ color }}>{metric.icon}</Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                      {metric.title}
                    </Typography>
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color, display: 'flex', alignItems: 'center', gap: 0.75 }}
                  >
                    {displayValue}
                    {metric.change !== undefined && metric.change !== null && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color:
                            metric.change > 0
                              ? brandColors.highInterest
                              : metric.change < 0
                              ? '#f44336'
                              : 'text.secondary'
                        }}
                      >
                        {metric.change > 0 ? '+' : ''}
                        {metric.change.toFixed(1)}%
                      </Typography>
                    )}
                  </Typography>
                  {metric.subtitle && (
                    <Typography variant="caption" color="text.secondary">
                      {metric.subtitle}
                    </Typography>
                  )}
                  <LinearProgress
                    variant="determinate"
                    value={value}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      backgroundColor: `${color}20`,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: color,
                        borderRadius: 2
                      }
                    }}
                  />
                </Box>
              </Grid>
            );
          })}
        </Grid>
      );

      const enhancedInsights =
        film.hasEnhancedData && film.demographics ? (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
              Enhanced Insights
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    Frequent Movie Goers
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', color: brandColors.primary }}>
                    {film.behavioral?.frequent_movie_goers_pct || 0}%
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    High Income
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', color: brandColors.success }}>
                    {film.demographics?.high_income_pct || 0}%
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    Northeast
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', color: brandColors.warning }}>
                    {film.geographic?.northeast_pct || 0}%
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    Organic Awareness
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', color: brandColors.info }}>
                    {film.openEnded?.organic_awareness_pct || 0}%
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        ) : null;

      const content = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {grid}
          {enhancedInsights}
        </Box>
      );

      if (canAccessDashboard) {
        return content;
      }

      return (
        <BlurredContent requiredTier={FILM_TIERS.BASIC} featureName="Film Dashboard" blurAmount={8}>
          {content}
        </BlurredContent>
      );
    };

    const tileFilm = {
      ...film,
      imdb_title: cleanFilmTitle(film.imdb_title || film.film_title),
      release_date_display: film.release_date ? formatDateForDisplay(film.release_date) : null
    };

    return (
      <FilmTile
        film={tileFilm}
        metrics={metrics}
        footerLines={footerLines}
        onClick={() => handleFilmClick(film.tt_id || film.imdb_id)}
        renderMetrics={renderMetricsSection}
        showSurveyDateInHeader={false}
        allowNavigation={canAccessDashboard}
        projectionSummary={projectionSummaries[film.imdb_id || film.tt_id]}
      />
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh'
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Box sx={{ textAlign: 'center' }}>
          <IconButton onClick={fetchData} color="primary" size="large">
            <Refresh />
          </IconButton>
          <Typography variant="body1" sx={{ mt: 1 }}>
            Click to retry
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
              Film Tracking Dashboard
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Survey insights and audience engagement metrics
            </Typography>
          </Box>
          <Tooltip title="Refresh data">
            <IconButton onClick={fetchData} color="primary" size="large">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>

        <FilmDashboardBanner stats={stats} />
      </Box>

      {/* Enhanced Filter Controls */}
      <Paper sx={{ p: 3, mb: 3 }}>
        {/* Filter Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList sx={{ color: brandColors.primary }} />
            <Typography variant="h6" sx={{ color: brandColors.primary }}>
              Filter & Sort Films
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={handleResetFilters}
            startIcon={<Refresh />}
          >
            Reset Filters
          </Button>
        </Box>

        {/* Date Range and Sort Controls */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Date Range */}
          <Grid item xs={12} md={3}>
            <TextField
              label="Start Date"
              type="date"
              value={pendingFilters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="End Date"
              type="date"
              value={pendingFilters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>

          {/* Sort Controls */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={pendingFilters.sortBy}
                label="Sort By"
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              >
                <MenuItem value="release_date">Release Date</MenuItem>
                <MenuItem value="awareness">Awareness</MenuItem>
                <MenuItem value="interest">Interest</MenuItem>
                <MenuItem value="heat">Heat Score</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Order</InputLabel>
              <Select
                value={pendingFilters.sortOrder}
                label="Order"
                onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Quick Filter Buttons */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
            Quick Date Ranges:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {Object.entries(quickRanges).map(([key, range]) => (
              <Chip
                key={key}
                label={range.label}
                onClick={() => handleQuickFilter(key)}
                variant="outlined"
                size="small"
                sx={{ 
                  '&:hover': { 
                    backgroundColor: brandColors.primary + '20' 
                  }
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Search Control */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Search Films"
              value={pendingFilters.searchTerm}
              onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              placeholder="Search by title or IMDB ID..."
              size="small"
              fullWidth
            />
          </Grid>
        </Grid>

        {/* Company Filters - NEW */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              multiple
              options={distributorOptions}
              value={pendingFilters.distributors}
              onChange={(event, newValue) => handleFilterChange('distributors', newValue)}
              loading={loadingCompanies}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Distributors"
                  placeholder="Select distributors..."
                  size="small"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...chipProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={option}
                      size="small"
                      {...chipProps}
                    />
                  );
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              multiple
              options={productionCompanyOptions}
              value={pendingFilters.productionCompanies}
              onChange={(event, newValue) => handleFilterChange('productionCompanies', newValue)}
              loading={loadingCompanies}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Production Companies"
                  placeholder="Select production companies..."
                  size="small"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...chipProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={option}
                      size="small"
                      {...chipProps}
                    />
                  );
                })
              }
            />
          </Grid>
        </Grid>

        {/* Apply Filters Button */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button
            variant="contained"
            onClick={handleApplyFilters}
            startIcon={<FilterList />}
            sx={{
              backgroundColor: brandColors.secondary,
              '&:hover': {
                backgroundColor: brandColors.primary
              }
            }}
          >
            Apply Filters
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            Adjust filters above, then click to apply
          </Typography>
        </Box>
        
        <Typography variant="body2" color="text.secondary">
          Showing {filteredFilms.length} of {films.length} films
          {metadata.release_date_range && (
            <span> • Release dates: {formatDateForDisplay(metadata.release_date_range.from)} to {formatDateForDisplay(metadata.release_date_range.to)}</span>
          )}
        </Typography>
      </Paper>

      {/* Films Grid */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          Film Performance Overview
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Click on any film to view detailed analytics and trends
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {filteredFilms.map((film) => (
          <Grid item xs={12} sm={6} lg={4} key={film.tt_id}>
            <FilmCard film={film} />
          </Grid>
        ))}
      </Grid>

      {/* Legend */}
      <Paper sx={{ p: 3, mt: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <Info sx={{ mr: 1 }} />
          Metric Definitions
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Awareness
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Percentage of respondents who are aware of the film
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Interest
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Percentage who want to see the film
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Theater Intent
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Percentage who want to see in theaters
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Whatshot fontSize="small" sx={{ color: '#ff5722' }} />
              Heat Score
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Overall buzz and engagement metric
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Willing to Pay
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Percentage willing to pay to watch
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default FilmTracking;

